import HistoryIcon from "@mui/icons-material/History";
import PublishIcon from "@mui/icons-material/Publish";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Title, useNotify } from "react-admin";
import {
  getConfig,
  getHistory,
  publish as publishRequest,
  rollback as rollbackRequest,
  saveDraft as saveDraftRequest,
} from "../../../service/adminLegalDisclosure/api/adminLegalDisclosureService";
import { LegalDisclosureForm } from "./LegalDisclosureForm";
import { LegalDisclosurePreview } from "./LegalDisclosurePreview";
import {
  emptyLegalDisclosureDraft,
  validateLegalDisclosureDraft,
} from "./validation";

type Operation = "load" | "save" | "publish" | "rollback" | null;

interface RequestError {
  status?: number;
  message?: string;
  body?: {
    code?: string;
    msg?: string;
    message?: string;
  };
}

const cloneDraft = (draft: API.LegalDisclosureDraft) =>
  JSON.parse(JSON.stringify(draft)) as API.LegalDisclosureDraft;

const formatDateTime = (value?: string) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const errorDetails = (error: unknown) => {
  const requestError = error as RequestError;
  if (
    requestError.status === 409 ||
    requestError.body?.code === "legalDisclosureVersionConflict"
  ) {
    return {
      conflict: true,
      message: "配置已被其他管理员更新，请重新加载后再操作。",
    };
  }
  return {
    conflict: false,
    message:
      requestError.body?.msg ||
      requestError.body?.message ||
      requestError.message ||
      "操作失败，请检查网络或后台日志。",
  };
};

export const LegalDisclosurePage = () => {
  const notify = useNotify();
  const [config, setConfig] = useState<API.LegalDisclosureConfig | null>(null);
  const [draft, setDraft] = useState<API.LegalDisclosureDraft>(
    emptyLegalDisclosureDraft()
  );
  const [revisions, setRevisions] = useState<API.LegalDisclosureRevision[]>([]);
  const [operation, setOperation] = useState<Operation>("load");
  const [conflict, setConflict] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [
    rollbackRevision,
    setRollbackRevision,
  ] = useState<API.LegalDisclosureRevision | null>(null);

  const applyConfig = useCallback((next: API.LegalDisclosureConfig) => {
    setConfig(next);
    setDraft(cloneDraft(next.draft || emptyLegalDisclosureDraft()));
    setRevisions(next.revisions || []);
    setConflict(false);
  }, []);

  const load = useCallback(async () => {
    setOperation("load");
    try {
      const [configResult, historyResult] = await Promise.all([
        getConfig(),
        getHistory(),
      ]);
      if (!configResult.data) {
        throw new Error("后台未返回运营主体声明配置");
      }
      applyConfig(configResult.data);
      if (historyResult.data) {
        setRevisions(historyResult.data);
      }
    } catch (error) {
      const details = errorDetails(error);
      notify(details.message, { type: "error" });
    } finally {
      setOperation(null);
    }
  }, [applyConfig, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const validate = () => {
    const errors = validateLegalDisclosureDraft(draft);
    if (errors.length > 0) {
      notify(errors.join("；"), { type: "error", autoHideDuration: 10000 });
      return false;
    }
    return true;
  };

  const runMutation = async (
    nextOperation: Exclude<Operation, "load" | null>,
    action: () => Promise<{
      data: API.LegalDisclosureConfig | undefined;
    }>,
    successMessage: string
  ) => {
    setOperation(nextOperation);
    try {
      const result = await action();
      if (!result.data) {
        throw new Error("后台未返回更新后的运营主体声明配置");
      }
      applyConfig(result.data);
      notify(successMessage, { type: "success" });
    } catch (error) {
      const details = errorDetails(error);
      setConflict(details.conflict);
      notify(details.message, { type: "error", autoHideDuration: 10000 });
    } finally {
      setOperation(null);
    }
  };

  const saveDraft = async () => {
    if (!config || !validate()) {
      return;
    }
    await runMutation(
      "save",
      async () =>
        saveDraftRequest({
          expectedVersion: config.version,
          draft,
        }),
      "草稿已保存，前台尚未发生变化。"
    );
  };

  const publish = async () => {
    setPublishOpen(false);
    if (!config || !validate()) {
      return;
    }
    await runMutation(
      "publish",
      async () =>
        publishRequest({
          expectedVersion: config.version,
          draft,
        }),
      draft.enabled
        ? "运营主体声明已发布并启用。"
        : "关闭状态已发布，前台不会展示运营主体声明。"
    );
  };

  const rollback = async () => {
    const revision = rollbackRevision;
    setRollbackRevision(null);
    if (!config || !revision) {
      return;
    }
    await runMutation(
      "rollback",
      async () =>
        rollbackRequest(
          { revisionId: revision.revisionId },
          { expectedVersion: config.version }
        ),
      `已回滚并发布版本 #${revision.revisionId}。`
    );
  };

  const busy = operation !== null;

  if (operation === "load" && !config) {
    return (
      <Box
        display="flex"
        minHeight={320}
        alignItems="center"
        justifyContent="center"
      >
        <Title title="运营主体声明" />
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1280, mx: "auto" }}>
      <Title title="运营主体声明" />
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4">运营主体声明</Typography>
            <Typography color="text.secondary">
              独立管理草稿、发布版本与历史回滚；未启用时不影响现有站点。
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              startIcon={<RefreshIcon />}
              disabled={busy}
              onClick={() => void load()}
            >
              重新加载
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              disabled={busy || !config}
              onClick={() => void saveDraft()}
            >
              {operation === "save" ? "保存中…" : "保存草稿"}
            </Button>
            <Button
              variant="contained"
              color={draft.enabled ? "primary" : "warning"}
              startIcon={<PublishIcon />}
              disabled={busy || !config}
              onClick={() => {
                if (validate()) {
                  setPublishOpen(true);
                }
              }}
            >
              {operation === "publish" ? "发布中…" : "发布"}
            </Button>
          </Stack>
        </Stack>

        {conflict && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void load()}>
                重新加载
              </Button>
            }
          >
            当前页面版本已过期。为防止覆盖其他管理员的修改，本次操作已终止。
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`编辑版本：${config?.version ?? "—"}`} />
              <Chip
                label={
                  config?.publishedRevisionId
                    ? `已发布：#${config.publishedRevisionId}`
                    : "尚未发布"
                }
                color={config?.publishedRevisionId ? "success" : "default"}
              />
              {config?.draftUpdatedBy && (
                <Chip
                  label={`草稿编辑人：${config.draftUpdatedBy.operatorName}`}
                  variant="outlined"
                />
              )}
              {config?.draftUpdatedAt && (
                <Chip
                  label={`草稿更新时间：${formatDateTime(
                    config.draftUpdatedAt
                  )}`}
                  variant="outlined"
                />
              )}
            </Stack>
          </CardContent>
        </Card>

        <LegalDisclosureForm
          draft={draft}
          disabled={busy}
          onChange={setDraft}
        />

        <LegalDisclosurePreview draft={draft} />

        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <HistoryIcon />
              <Typography variant="h6">发布历史</Typography>
            </Stack>
            {revisions.length === 0 ? (
              <Typography color="text.secondary">尚无发布记录。</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>版本</TableCell>
                      <TableCell>动作</TableCell>
                      <TableCell>发布人</TableCell>
                      <TableCell>发布时间</TableCell>
                      <TableCell>状态</TableCell>
                      <TableCell align="right">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {revisions.map((revision) => {
                      const current =
                        revision.revisionId === config?.publishedRevisionId;
                      return (
                        <TableRow key={revision.revisionId}>
                          <TableCell>#{revision.revisionId}</TableCell>
                          <TableCell>
                            {revision.action === "ROLLBACK"
                              ? "回滚发布"
                              : "发布"}
                            {revision.sourceRevisionId
                              ? `（来源 #${revision.sourceRevisionId}）`
                              : ""}
                          </TableCell>
                          <TableCell>
                            {revision.publishedBy.operatorName ||
                              revision.publishedBy.operatorId}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(revision.publishedAt)}
                          </TableCell>
                          <TableCell>
                            {current && (
                              <Chip
                                size="small"
                                label="当前版本"
                                color="success"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              disabled={busy || current}
                              onClick={() => setRollbackRevision(revision)}
                            >
                              回滚并发布
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)}>
        <DialogTitle>确认发布运营主体声明</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {draft.enabled
              ? "发布后，当前实例的前台将展示这份运营主体声明与绑定的法律文档。请确认预览内容已经过法律审核。"
              : "本次发布会保持功能关闭，前台不会展示运营主体声明。"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>取消</Button>
          <Button variant="contained" onClick={() => void publish()}>
            确认发布
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(rollbackRevision)}
        onClose={() => setRollbackRevision(null)}
      >
        <DialogTitle>确认回滚并重新发布</DialogTitle>
        <DialogContent>
          <DialogContentText>
            将版本 #{rollbackRevision?.revisionId}{" "}
            的完整内容重新发布为新版本，历史记录不会被删除。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackRevision(null)}>取消</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => void rollback()}
          >
            确认回滚
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegalDisclosurePage;
